import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaHouse, FaXmark } from 'react-icons/fa6';

import { PageBreadcrumbs, PageNavigation } from '@client/toolkit';

import {
  ECONOMY_NAV_ITEM_ACTIVE_CLASS,
  ECONOMY_NAV_ITEM_CLASS,
  ECONOMY_SIDEBAR_WIDTH,
} from '../economy-theme';

export interface PortfolioSidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

/**
 * Scope rail for the economy pages, built like the reports menu. The portfolio-wide view
 * is the only scope that exists, so the single entry is a static "you are here" row rather
 * than a link — hence no trailing arrow, which would promise a navigation that is not there.
 */
export function PortfolioSidebar({ isOpen, setIsOpen }: PortfolioSidebarProps) {
  const { t } = useTranslation('economy');
  const { t: tGlobal } = useTranslation('global');

  return (
    <motion.div
      className="flex flex-col gap-5 overflow-hidden"
      initial={{ x: 0 }}
      animate={{ x: isOpen ? 0 : -ECONOMY_SIDEBAR_WIDTH }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <PageNavigation>
        <div className="relative">
          <PageBreadcrumbs>
            <PageBreadcrumbs.Link>{tGlobal('navigation.overview')}</PageBreadcrumbs.Link>
            <PageBreadcrumbs.Link>{tGlobal('navigation.navbar.economy')}</PageBreadcrumbs.Link>
          </PageBreadcrumbs>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label={t('sidebar.collapse')}
            className="absolute right-0 top-1/2 hidden -translate-y-1/2 rounded-full transition-colors hover:bg-brand-primary-hover lg:block"
          >
            <FaXmark className="h-4 w-4 text-white" />
          </button>
        </div>
      </PageNavigation>

      <nav aria-label={tGlobal('navigation.navbar.economy')}>
        <div aria-current="page" className={classNames(ECONOMY_NAV_ITEM_CLASS, ECONOMY_NAV_ITEM_ACTIVE_CLASS)}>
          <FaHouse className="h-3 w-3 shrink-0" />
          {t('sidebar.portfolio')}
        </div>
      </nav>
    </motion.div>
  );
}

export default PortfolioSidebar;
